import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  InstructionWriteBody,
  ScheduleWriteBody,
  WorkflowDefinition,
  WorkflowExecution,
  WorkflowInstruction,
  WorkflowSchedule,
  WorkflowScope,
} from '../models/workflow.types'

import { appHttp, unwrap } from '@/shared/http'

const OWNED_ERRORS = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
} as const

function requestConfig(signal?: AbortSignal, params?: Record<string, unknown>): AxiosRequestConfig {
  const config: AxiosRequestConfig = { ...OWNED_ERRORS }
  if (signal) config.signal = signal
  if (params) config.params = params
  return config
}

function segment(value: number | string): string {
  return encodeURIComponent(String(value))
}

function base(scope: WorkflowScope): string {
  return `/accounts/${segment(scope.accountId)}/workspaces/${segment(scope.workspaceId)}/workflow`
}

/** Thin transport for the measured django-rag workflow wire. */
export class WorkflowApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async listDefinitions(scope: WorkflowScope, signal?: AbortSignal): Promise<WorkflowDefinition[]> {
    const response = await this.http.get<{ workflows?: WorkflowDefinition[] }>(
      `${base(scope)}/workflows/active/`,
      requestConfig(signal),
    )
    return unwrap(response).workflows ?? []
  }

  // NOTE (ORP-20398): `GET /results/` and `GET /workflow/{id}/results/` are live on the backend
  // (`agentic_workflow/urls.py:22-23`, paginated under BE-WFL-04) but have no v4 consumer — the
  // detail/list screens they fed were retired by ORP-20033 and the surviving weekly result was
  // rebuilt in module 28 on a different wire. The transport methods were removed with the rest of
  // the dead layer; re-add them from the backend contract if a result surface is ever commissioned.

  async listSchedules(scope: WorkflowScope, signal?: AbortSignal): Promise<WorkflowSchedule[]> {
    const response = await this.http.get<{ schedules?: WorkflowSchedule[] }>(
      `${base(scope)}/workflow-schedules/`,
      requestConfig(signal),
    )
    return unwrap(response).schedules ?? []
  }

  async createSchedule(scope: WorkflowScope, body: ScheduleWriteBody): Promise<WorkflowSchedule> {
    return unwrap(
      await this.http.post<WorkflowSchedule>(
        `${base(scope)}/workflow-schedules/`,
        body,
        OWNED_ERRORS,
      ),
    )
  }

  async updateSchedule(
    scope: WorkflowScope,
    scheduleId: number | string,
    body: ScheduleWriteBody,
  ): Promise<WorkflowSchedule> {
    return unwrap(
      await this.http.put<WorkflowSchedule>(
        `${base(scope)}/workflow-schedules/${segment(scheduleId)}/`,
        body,
        OWNED_ERRORS,
      ),
    )
  }

  async pauseSchedule(
    scope: WorkflowScope,
    scheduleId: number | string,
  ): Promise<WorkflowSchedule> {
    return unwrap(
      await this.http.post<WorkflowSchedule>(
        `${base(scope)}/workflow-schedules/${segment(scheduleId)}/pause/`,
        undefined,
        OWNED_ERRORS,
      ),
    )
  }

  async resumeSchedule(
    scope: WorkflowScope,
    scheduleId: number | string,
  ): Promise<WorkflowSchedule> {
    return unwrap(
      await this.http.post<WorkflowSchedule>(
        `${base(scope)}/workflow-schedules/${segment(scheduleId)}/resume/`,
        undefined,
        OWNED_ERRORS,
      ),
    )
  }

  async deleteSchedule(scope: WorkflowScope, scheduleId: number | string): Promise<void> {
    await this.http.delete(
      `${base(scope)}/workflow-schedules/${segment(scheduleId)}/`,
      OWNED_ERRORS,
    )
  }

  async runNow(scope: WorkflowScope, scheduleId: number | string): Promise<WorkflowExecution> {
    return unwrap(
      await this.http.post<WorkflowExecution>(
        `${base(scope)}/workflow-schedules/${segment(scheduleId)}/run-now/`,
        { input_parameters: {} },
        OWNED_ERRORS,
      ),
    )
  }

  async listInstructions(
    scope: WorkflowScope,
    signal?: AbortSignal,
  ): Promise<WorkflowInstruction[]> {
    const response = await this.http.get<{ instructions?: WorkflowInstruction[] }>(
      `${base(scope)}/llm-instructions/`,
      requestConfig(signal, { scope_type: 'workspace_all' }),
    )
    return unwrap(response).instructions ?? []
  }

  async createInstruction(
    scope: WorkflowScope,
    body: InstructionWriteBody,
  ): Promise<WorkflowInstruction> {
    return unwrap(
      await this.http.post<WorkflowInstruction>(
        `${base(scope)}/llm-instructions/`,
        body,
        OWNED_ERRORS,
      ),
    )
  }

  async updateInstruction(
    scope: WorkflowScope,
    instructionId: number | string,
    body: InstructionWriteBody,
  ): Promise<WorkflowInstruction> {
    return unwrap(
      await this.http.put<WorkflowInstruction>(
        `${base(scope)}/llm-instructions/${segment(instructionId)}/`,
        body,
        OWNED_ERRORS,
      ),
    )
  }

  async deleteInstruction(scope: WorkflowScope, instructionId: number | string): Promise<void> {
    await this.http.delete(
      `${base(scope)}/llm-instructions/${segment(instructionId)}/`,
      OWNED_ERRORS,
    )
  }
}

export const workflowApi = new WorkflowApi()
